'use client';

import { useEffect } from 'react';

/**
 * CrashMonitor – глобален debug monitor за iOS Safari crash диагностика.
 *
 * Улавя:
 *  1. window.onerror – некешнати JS грешки
 *  2. unhandledrejection – пропуснати Promise.catch
 *  3. pagehide / visibilitychange – детектира дали страницата е била убита от OS
 *  4. pageshow (persisted) – детектира BFCache restore (= Safari е убил таба и го е възстановил)
 *  5. Периодичен memory snapshot (само Chrome/Edge – Safari няма performance.memory)
 *
 * Всички логове се записват в sessionStorage за да оцелеят при soft reload.
 * При краш от iOS Safari (таб убит от WebKit) – sessionStorage се губи,
 * затова пишем и в localStorage с таймстамп.
 */

const STORAGE_KEY = 'crash_monitor_log';
const LS_KEY = 'crash_monitor_persistent';
const MAX_ENTRIES = 50;
const HEARTBEAT_MS = 3000;
/** Записът „жив“ е от друг таб, ако същият таб пише пак до ~2 heartbeat-а. */
const ALIVE_CHECK_MS = 7500; // 2.5 heartbeat-а
const CRASH_WINDOW_MS = 5 * 60 * 1000;
const ACTIVE_TYPES = new Set(['page_active', 'upload_step', 'memory_snapshot']);
/** Едно зареждане = един таб: localStorage е общ за табовете, затова всеки запис носи номера. */
const BASE36 = 36;
const TAB_ID = Math.random().toString(BASE36).slice(2, 10);

function getTimestamp() {
  return new Date().toISOString();
}

function logEvent(type, data) {
  const entry = { ts: getTimestamp(), type, tab: TAB_ID, ...data };

  // Console – disabled for dev clarity (re-enable for crash debugging)
  // console.warn(`[CrashMonitor] ${type}`, entry);

  // sessionStorage – оцелява при JS-triggered reload, но НЕ при OOM crash
  try {
    const existing = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    existing.push(entry);
    if (existing.length > MAX_ENTRIES) {
      existing.splice(0, existing.length - MAX_ENTRIES);
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // storage full or unavailable
  }

  // localStorage – оцелява дори при OOM crash (ако write-ът завърши преди kill)
  try {
    const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    existing.push(entry);
    if (existing.length > MAX_ENTRIES) {
      existing.splice(0, existing.length - MAX_ENTRIES);
    }
    localStorage.setItem(LS_KEY, JSON.stringify(existing));
  } catch {
    // quota exceeded
  }
}

/**
 * Последният запис на всеки ДРУГ таб; кандидат за срив = завършва с „активен“ запис (без
 * page_hidden / page_unload) в последните 5 мин. Стари записи без `tab` = един общ таб.
 */
export function findCrashCandidates(logs, now, ownTab) {
  const lastByTab = new Map();
  (logs || []).forEach((entry) => lastByTab.set(entry.tab ?? 'legacy', entry));
  return [...lastByTab.entries()]
    .filter(([tab, last]) => tab !== ownTab && ACTIVE_TYPES.has(last.type))
    .filter(([, last]) => now - new Date(last.ts).getTime() < CRASH_WINDOW_MS)
    .map(([tab, last]) => ({ tab, last }));
}

/** Табът писал ли е след `sinceTs` → жив (друг отворен таб), не срив. */
export function wroteSince(logs, tab, sinceTs) {
  return (logs || []).some((entry) => (entry.tab ?? 'legacy') === tab && entry.ts > sinceTs);
}

function readPersistentLogs() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * Предишен срив: таб, чийто поток е спрял без page_hidden. Проверява се след ~2 heartbeat-а –
 * друг ОТВОРЕН таб (общ localStorage) продължава да пише и не е срив (стрес тест 07.10).
 */
function checkPreviousCrash() {
  const candidates = findCrashCandidates(readPersistentLogs(), Date.now(), TAB_ID);
  if (candidates.length === 0) {
    return undefined;
  }
  const timer = setTimeout(() => {
    const logs = readPersistentLogs();
    candidates
      .filter(({ tab, last }) => !wroteSince(logs, tab, last.ts))
      .forEach(({ last }) => {
        console.error(
          '[CrashMonitor] ПРЕДИШЕН CRASH ДЕТЕКТИРАН! Последен запис преди краша:',
          last,
        );
        console.error('[CrashMonitor] Пълен лог преди краша:', logs.slice(-10));
      });
  }, ALIVE_CHECK_MS);
  return () => clearTimeout(timer);
}

export default function CrashMonitor() {
  useEffect(() => {
    // При зареждане – провери дали предишната сесия е крашнала
    const cancelCrashCheck = checkPreviousCrash();
    logEvent('page_load', {
      url: window.location.href,
      ua: navigator.userAgent,
      deviceMemory: navigator.deviceMemory || 'N/A',
      hardwareConcurrency: navigator.hardwareConcurrency || 'N/A',
      screen: `${screen.width}x${screen.height}@${window.devicePixelRatio}x`,
    });

    // 1. Некешнати JS грешки
    const handleError = (event) => {
      logEvent('js_error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack?.slice(0, 500),
      });
    };

    // 2. Unhandled promise rejections
    const handleRejection = (event) => {
      const { reason } = event;
      logEvent('unhandled_rejection', {
        message: reason?.message || String(reason),
        stack: reason?.stack?.slice(0, 500),
      });
    };

    // 3. Page hide (таб скрит, навигация, или OS убива процеса)
    const handlePageHide = (event) => {
      logEvent('page_hidden', {
        persisted: event.persisted,
        // persisted=true → BFCache, persisted=false → реално затваряне/навигация
      });
    };

    // 4. Page show – детектира BFCache restore (= Safari е убил таба и го е възстановил)
    const handlePageShow = (event) => {
      if (event.persisted) {
        logEvent('page_restored_from_bfcache', {
          note: 'Safari може да е убил таба и го е възстановил от BFCache',
        });
      }
    };

    // 5. Visibility change
    const handleVisibility = () => {
      logEvent(document.visibilityState === 'hidden' ? 'tab_hidden' : 'tab_visible', {});
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);

    // Периодичен heartbeat – ако тази серия спре без page_hidden,
    // при следващо зареждане checkPreviousCrash() ще го детектира
    const heartbeat = setInterval(() => {
      // Skip heartbeat when tab is hidden – prevents false crash detection
      if (document.visibilityState === 'hidden') {
        return;
      }
      const mem = {};
      // performance.memory е само в Chrome, но проверяваме
      if (typeof performance !== 'undefined' && performance.memory) {
        const m = performance.memory;
        const MB = 1024 * 1024;
        mem.usedMB = Math.round(m.usedJSHeapSize / MB);
        mem.totalMB = Math.round(m.totalJSHeapSize / MB);
        mem.limitMB = Math.round(m.jsHeapSizeLimit / MB);
      }
      logEvent('page_active', mem);
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(heartbeat);
      cancelCrashCheck?.();
      // Log clean unmount so next load doesn't think it was a crash (covers HMR / navigation)
      logEvent('page_unload', { reason: 'component_cleanup' });
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return null; // invisible component
}

/**
 * Ръчен лог за upload стъпки – извикай от ImageZone handleFileChange.
 * Позволява да се проследи точно до коя стъпка е стигнал upload-ът преди crash.
 */
export function logUploadStep(step, extra = {}) {
  logEvent('upload_step', { step, ...extra });
}

/**
 * Извлича записаните логове от localStorage за debug.
 * Извикай в Safari Web Inspector конзолата: CrashMonitor.getLogs()
 */
export function getCrashLogs() {
  return readPersistentLogs();
}

/**
 * Изчиства логовете.
 */
export function clearCrashLogs() {
  try {
    localStorage.removeItem(LS_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Expose на window за достъп от конзолата
if (typeof window !== 'undefined') {
  window.CrashMonitor = { getLogs: getCrashLogs, clearLogs: clearCrashLogs };
}
